/**
 * Shared Filter Utilities
 *
 * Canonical helpers for working with filter values across table components
 * and query builders. All date-range parsing and active-filter detection
 * logic lives here to avoid duplication.
 */

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Sentinel value used by filter dropdowns to represent "no filter selected".
 */
export const NO_FILTER_VALUE = "all" as const;

// =============================================================================
// TYPES
// =============================================================================

/**
 * A date range filter with ISO-string boundaries.
 * Canonical definition shared across UI components and query builders.
 */
export interface DateRangeFilter {
  from: string;
  to: string;
}

// =============================================================================
// DATE VALIDATION
// =============================================================================

const REASONABLE_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MIN_YEAR = 1900;

/**
 * Check whether a date string looks like a complete, reasonable YYYY-MM-DD
 * value. Empty strings are accepted (they represent "no selection"). Partial
 * years emitted by native `<input type="date">` during typing (e.g. 0002,
 * 0020) are rejected.
 */
export function isReasonableDate(value: string): boolean {
  if (value === "") return true;
  if (!REASONABLE_DATE_RE.test(value)) return false;
  const year = Number.parseInt(value.substring(0, 4), 10);
  return year >= MIN_YEAR;
}

/**
 * Message for a complete date the range input should reject in place.
 * Empty and incomplete values return null so typing is not interrupted.
 * `today` is a local `YYYY-MM-DD` string.
 */
export function getDateInputRejection(
  value: string,
  today: string,
): string | null {
  if (value === "" || !REASONABLE_DATE_RE.test(value)) return null;
  const year = Number.parseInt(value.substring(0, 4), 10);
  if (year < MIN_YEAR) return "Dates before 1900 are not allowed.";
  if (today && value > today) return "This date is in the future.";
  return null;
}

/** Shown when a start date falls after its end date. */
export const DATE_RANGE_ORDER_ERROR =
  "Start date must be on or before end date.";

/**
 * Message when a complete start date falls after the end date, else null.
 * Both values are `YYYY-MM-DD`; empty or incomplete values return null.
 */
export function getDateRangeOrderError(
  from: string,
  to: string,
): string | null {
  if (!REASONABLE_DATE_RE.test(from) || !REASONABLE_DATE_RE.test(to)) {
    return null;
  }
  return from > to ? DATE_RANGE_ORDER_ERROR : null;
}

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Runtime type guard for {@link DateRangeFilter}.
 */
export function isDateRangeObject(value: unknown): value is DateRangeFilter {
  return (
    typeof value === "object" &&
    value !== null &&
    "from" in value &&
    "to" in value &&
    typeof (value as Record<string, unknown>).from === "string" &&
    typeof (value as Record<string, unknown>).to === "string"
  );
}

// =============================================================================
// PARSING
// =============================================================================

/**
 * Attempt to parse a string as a JSON-encoded {@link DateRangeFilter}.
 *
 * @returns The parsed object on success, or `null` if the string is not a
 *          valid JSON date range.
 */
export function parseDateRange(value: string): DateRangeFilter | null {
  try {
    const parsed: unknown = JSON.parse(value);
    if (isDateRangeObject(parsed)) {
      return { from: parsed.from, to: parsed.to };
    }
  } catch {
    // Not valid JSON
  }
  return null;
}

/**
 * Normalize a filter value that may be a {@link DateRangeFilter} object, a
 * JSON string encoding one, or `undefined` into a guaranteed
 * {@link DateRangeFilter}.
 */
export function normalizeDateRangeValue(
  value: string | string[] | DateRangeFilter | undefined,
): DateRangeFilter {
  if (isDateRangeObject(value)) {
    return value;
  }

  if (typeof value === "string" && value.trim() !== "") {
    const parsed = parseDateRange(value);
    if (parsed) {
      return parsed;
    }
  }

  return { from: "", to: "" };
}

// =============================================================================
// SERIALIZATION
// =============================================================================

/**
 * Flatten a filter-values map that may contain `{ from, to }` date-range
 * objects into a shape that can be passed to server-side query hooks (which
 * only accept `string | string[]` values). Date-range objects are
 * JSON-stringified; arrays and strings are passed through.
 */
export function serializeFilterValues(
  filters: Record<string, string | string[] | DateRangeFilter>,
): Record<string, string | string[]> {
  const result: Record<string, string | string[]> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (Array.isArray(value) || typeof value === "string") {
      result[key] = value;
    } else {
      result[key] = JSON.stringify(value);
    }
  }
  return result;
}

/**
 * Collapse array values on single-select keys to the first entry.
 * Empty / falsy first values drop the key so persisted multi-select state
 * cannot keep applying `in:` queries against a single-select chip.
 */
export function coerceSingleSelectFilters<T extends Record<string, unknown>>(
  filters: T,
  ids: readonly string[],
): T {
  let next: T | undefined;
  for (const id of ids) {
    const value = filters[id];
    if (!Array.isArray(value)) continue;
    next ??= { ...filters };
    const first = value[0];
    if (!first) delete next[id];
    else (next as Record<string, unknown>)[id] = first;
  }
  return next ?? filters;
}

// =============================================================================
// ACTIVE-FILTER DETECTION
// =============================================================================

/**
 * Determine whether a filter value represents an active (non-default) filter.
 *
 * - Empty strings, `undefined`, and the {@link NO_FILTER_VALUE} sentinel are
 *   considered inactive.
 * - JSON-encoded date ranges where both `from` and `to` are empty are
 *   considered inactive.
 * - All other non-empty strings are considered active.
 * - Object date ranges with at least one non-empty bound are active.
 */
export function isActiveFilterValue(
  filterValue: string | string[] | DateRangeFilter | undefined,
): boolean {
  if (Array.isArray(filterValue)) {
    return filterValue.length > 0;
  }

  if (typeof filterValue === "string") {
    if (!filterValue || filterValue === NO_FILTER_VALUE) {
      return false;
    }

    const dateRange = parseDateRange(filterValue);
    if (dateRange) {
      return dateRange.from !== "" || dateRange.to !== "";
    }

    return true;
  }

  return Boolean(
    filterValue && (filterValue.from !== "" || filterValue.to !== ""),
  );
}
